import {
    createBaseThemeOptions,
    createUnifiedTheme,
    genPageTheme,
    palettes,
    shapes,
} from '@backstage/theme';

// Design token: borderRadius scale
// Use these named steps — never hardcode raw px values.
export const radius = {
    sm:   4,    // slight rounding
    md:   8,    // card/panel corners — primary default for marketplace cards
    lg:  16,    // dialog / sheet corners
    pill: 9999, // fully rounded — replaces borderRadius: 25 / 50
} as const;

export const revenium = createUnifiedTheme({
    ...createBaseThemeOptions({
        palette: {
            ...palettes.light,
            primary: {
                main: '#1e3a8a', // Indiana government blue
            },
            secondary: {
                main: '#2563eb', // Brand blue accent — AA on white (5.17:1) [A11Y-01 D-01 D-02]
            },
            error: {
                main: '#b91c1c', // Darkened red — AA on white (6.47:1) and on errorBackground #fef2f2 (5.91:1) [A11Y-01 D-03]
            },
            warning: {
                main: '#b45309', // Darkened amber — AA on white (5.02:1) and on #fffbeb (4.84:1) [A11Y-01 D-03]
            },
            info: {
                main: '#0369a1', // Darkened sky blue — AA on white (5.93:1) and on #f0f9ff (5.57:1) [A11Y-01 D-03]
            },
            success: {
                main: '#047857', // Darkened emerald — AA on white (5.48:1) and on #f0fdf4 (5.24:1) [A11Y-01 D-03]
            },
            background: {
                default: '#ffffff', // Clean white background
                paper: '#f8fafc', // Very light gray for cards/papers
            },
            banner: {
                info: '#0ea5e9',
                error: '#dc2626',
                text: '#1e3a8a',
                link: '#2563eb', // AA text link on white (5.17:1) [A11Y-01 D-01]
            },
            errorBackground: '#fef2f2', // Light red background
            warningBackground: '#fffbeb', // Light amber background
            infoBackground: '#f0f9ff', // Light blue background
            navigation: {
                background: '#1e3a8a', // Indiana government blue
                indicator: '#60a5fa', // Light blue indicator — passes 3:1 non-text on navy #1e3a8a (4.07:1) [A11Y-01 D-01]
                color: '#ffffff', // White text on blue background
                selectedColor: '#f1f5f9', // Light gray for selections
                navItem: {
                    // FND-04: replaces Backstage's #404040 gray fallback with the brand
                    // accent blue so hover/selected reads clearly on the navy sidebar.
                    // #2563eb: white text on it = 5.17:1 — AA for text on interactive bg [A11Y-01 D-01]
                    hoverBackground: '#2563eb',
                },
                // UX-05 / ENT-79: sidebar gradient + group separation tokens.
                // Deliberately DEEPER navy than the page headers (#1e3a8a→#1e40af) so the
                // sidebar reads as a distinct, anchoring surface that the headers
                // sit against — a clear contrast boundary, not a blend.
                sidebarGradient: 'linear-gradient(180deg, #0f1f4d 0%, #0c1840 55%, #0a1330 100%)',
                // Subtle dark separator line on the deep-navy gradient — a touch lighter
                // than the navy so it reads as a quiet groove (close to the Backstage
                // default #383838 the user liked), never a bright white bar.
                groupSeparatorColor: 'rgba(255, 255, 255, 0.10)',
            },
        },
    }),
    defaultPageTheme: 'home',
    fontFamily: "'Roboto', 'Arial', sans-serif", // Futuristic corporate font
    pageTheme: {
        home: genPageTheme({ colors: ['#1e3a8a', '#1e40af'], shape: shapes.wave }),
        documentation: genPageTheme({
            colors: ['#1e3a8a', '#1e40af'],
            shape: shapes.wave2,
        }),
        tool: genPageTheme({ colors: ['#1e3a8a', '#1e40af'], shape: shapes.round }),
        service: genPageTheme({
            colors: ['#1e3a8a', '#1e40af'],
            shape: shapes.wave,
        }),
        website: genPageTheme({
            colors: ['#1e3a8a', '#1e40af'],
            shape: shapes.wave,
        }),
        library: genPageTheme({
            colors: ['#1e3a8a', '#1e40af'],
            shape: shapes.wave,
        }),
        other: genPageTheme({ colors: ['#1e3a8a', '#1e40af'], shape: shapes.wave }),
        app: genPageTheme({ colors: ['#1e3a8a', '#1e40af'], shape: shapes.wave }),
        apis: genPageTheme({ colors: ['#1e3a8a', '#1e40af'], shape: shapes.wave }),
    },
    components: {
        // VIS-05 / D-11 / A11Y-05: Full-height layout chain.
        // Root-caused via live Docker DevTools (Task 1 checkpoint, APPROVED):
        // the height chain html{100%} → body{100%} → #root breaks at #root, which
        // has NO height rule. Below it SidebarPage/Content collapse to intrinsic
        // content height, so InfoCard gridItem cards' `height: calc(100% - 10px)`
        // resolve against a zero-height parent → content bunches in the upper half.
        // #root (packages/app/public/index.html, mounted in index.tsx) is OUTSIDE
        // the React tree Root.tsx renders, so it can only be reached via a global
        // style — hence a MuiCssBaseline @global override rather than a Root.tsx
        // makeStyles class. Screen-first: no @media print rule (SidebarPage root
        // already sets `@media print { padding: 0 }`, InfoCard gridItem already
        // sets `@media print { height: auto }` — this does not fight them).
        MuiCssBaseline: {
            styleOverrides: {
                '@global': {
                    // The missing link in the height chain.
                    '#root': {
                        height: '100%',
                    },
                },
            },
        },
        // Phase-1 (FND-04): hover + focus-visible states for sidebar items.
        // Do NOT merge with BackstageSidebar below — items must NOT carry the gradient.
        BackstageSidebarItem: {
            styleOverrides: {
                root: ({ theme }: { theme: any }) => ({
                    // UX-05 / ENT-79: more breathing room between nav items so the rail
                    // reads as roomy, not cramped. theme.spacing keeps it token-driven.
                    marginTop: theme.spacing(0.5),
                    marginBottom: theme.spacing(0.5),
                    '&:hover': {
                        backgroundColor: theme.palette.navigation?.navItem?.hoverBackground,
                    },
                    '&:focus-visible': {
                        backgroundColor: theme.palette.navigation?.navItem?.hoverBackground,
                        outline: `2px solid ${theme.palette.navigation?.selectedColor}`,
                        outlineOffset: -2,
                    },
                }),
                // UX-05 / ENT-79: let the item label use more of the sidebar width
                // (default truncates early); larger, clearer nav text.
                label: {
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                },
            },
        },
        // UX-05 / ENT-79: Apply the navy gradient to the sidebar CONTAINER.
        // Class key is `drawer` (verified against @backstage/core-components@0.16.3
        // Bar.esm.js — the makeStyles call uses { name: "BackstageSidebar" } and the
        // single class key is `drawer`, not `root`). The gradient lives here so it
        // flows continuously behind all items at every scroll position.
        BackstageSidebar: {
            styleOverrides: {
                drawer: ({ theme }: { theme: any }) => ({
                    background: theme.palette.navigation?.sidebarGradient,
                }),
            },
        },
        // VIS-05 / D-11 / A11Y-05: app-shell wrapper must fill the viewport.
        // VERIFIED (Task 1 checkpoint, and against @backstage/core-components
        // Sidebar/Page.esm.js:9-33): SidebarPage renders a MUI Box with makeStyles
        // { name: "BackstageSidebarPage" }; its root class is `width:100%` with NO
        // height. It is makeStyles (NOT styled()), so this styleOverrides.root
        // REACHES it. minHeight:100vh makes the shell fill the viewport; the flex
        // column lets the content area (BackstageContent, flex:1 below) stretch to
        // fill the remaining height. 100vh/flex are structural, not magic spacing.
        BackstageSidebarPage: {
            styleOverrides: {
                root: {
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            },
        },
        // VIS-05 / D-11 / A11Y-05: content area stretches to fill the flex column.
        // VERIFIED (against core-components Content/Content.esm.js:5-29): Content is
        // an <article> with makeStyles { name: "BackstageContent" }; its root has
        // padding but NO flex/height. It is makeStyles, so this override REACHES it.
        // flex:1 makes it consume the remaining height of the SidebarPage flex
        // column above, giving InfoCard gridItem's calc(100% - 10px) a real
        // height-bearing ancestor to resolve against.
        BackstageContent: {
            styleOverrides: {
                root: {
                    flex: 1,
                },
            },
        },
    },
});
